// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { RxCollection } from 'rxdb';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Mutation state tracking
 */
export interface MutationState<T> {
  /** Result data from successful mutation */
  data: T | null;
  /** True while mutation is executing */
  isLoading: boolean;
  /** Error object if mutation failed, null otherwise */
  error: Error | null;
}

/**
 * Mutation operator configuration
 */
export interface MutationOptions<TData, TVariables> {
  /** Function that performs the mutation on the collection */
  mutationFn: (collection: RxCollection, variables: TVariables) => Promise<TData>;
  /** Optional callback invoked on successful mutation */
  onSuccess?: (data: TData, variables: TVariables) => void | Promise<void>;
  /** Optional callback invoked on mutation error */
  onError?: (error: Error, variables: TVariables) => void | Promise<void>;
}

/**
 * Framework-agnostic mutation operator
 *
 * Wraps RxDB write operations with state management and callbacks.
 * Works in any environment (Node.js, Browser, React Native, Electron).
 *
 * @example
 * ```typescript
 * // Create mutation operator
 * const createTask = new MutationOperator(collection, {
 *   mutationFn: async (col, data) => {
 *     await col.insert(data);
 *     return data;
 *   },
 *   onSuccess: (data) => console.log('Created:', data.id)
 * });
 *
 * // Execute imperatively
 * const task = await createTask.execute({ title: 'New Task' });
 *
 * // Or subscribe reactively
 * createTask.state$.subscribe(state => {
 *   if (state.isLoading) console.log('Saving...');
 *   if (state.data) console.log('Saved!');
 * });
 * ```
 */
export class MutationOperator<TData, TVariables> {
  private collection: RxCollection;
  private mutationFn: (collection: RxCollection, variables: TVariables) => Promise<TData>;
  private onSuccess?: (data: TData, variables: TVariables) => void | Promise<void>;
  private onError?: (error: Error, variables: TVariables) => void | Promise<void>;
  private stateSubject: BehaviorSubject<MutationState<TData>>;

  constructor(collection: RxCollection, options: MutationOptions<TData, TVariables>) {
    this.collection = collection;
    this.mutationFn = options.mutationFn;
    this.onSuccess = options.onSuccess;
    this.onError = options.onError;
    this.stateSubject = new BehaviorSubject<MutationState<TData>>({
      data: null,
      isLoading: false,
      error: null,
    });
  }

  /**
   * Execute mutation imperatively
   *
   * Performs the mutation, updates state, and invokes callbacks.
   * Returns Promise for imperative usage in scripts and CLI tools.
   *
   * @param variables - Input variables for the mutation
   * @returns Promise resolving to mutation result data
   * @throws Error if mutation fails
   */
  async execute(variables: TVariables): Promise<TData> {
    this.stateSubject.next({
      data: null,
      isLoading: true,
      error: null,
    });

    try {
      const result = await this.mutationFn(this.collection, variables);

      this.stateSubject.next({
        data: result,
        isLoading: false,
        error: null,
      });

      await this.onSuccess?.(result, variables);
      return result;
    } catch (error: any) {
      const err = error instanceof Error ? error : new Error(String(error));

      this.stateSubject.next({
        data: null,
        isLoading: false,
        error: err,
      });

      await this.onError?.(err, variables);
      throw err;
    }
  }

  /**
   * Get reactive state observable
   *
   * Subscribe to this in UI frameworks for automatic state updates.
   *
   * @returns Observable that emits MutationState on every state change
   */
  get state$(): Observable<MutationState<TData>> {
    return this.stateSubject.asObservable();
  }

  /**
   * Get current mutation state synchronously
   *
   * @returns Current MutationState value
   */
  get value(): MutationState<TData> {
    return this.stateSubject.value;
  }

  /**
   * Reset mutation state to initial values
   */
  reset(): void {
    this.stateSubject.next({
      data: null,
      isLoading: false,
      error: null,
    });
  }

  /**
   * Cleanup and complete observables
   *
   * Call this when the mutation operator is no longer needed.
   */
  destroy(): void {
    this.stateSubject.complete();
  }
}
